/**
 * Single client-side interpretation of ticket payment balance.
 * Prefer server fields `paidTotal` / `balanceDue` (maintained from payments).
 */

export type TicketBalanceInput = {
  total?: string | number | null;
  paidTotal?: string | number | null;
  balanceDue?: string | number | null;
  /** @deprecated never prefer this — deposits live in payments + paidTotal */
  depositPaid?: string | number | null;
  depositCollected?: string | number | null;
};

export type TicketBalance = {
  orderTotal: number;
  paidTotal: number;
  /** Positive = amount owed; zero = paid in full; negative = credit owed customer */
  balanceDue: number;
  amountToCollect: number;
  creditOwed: number;
  isPaidInFull: boolean;
  isOverpaid: boolean;
};

function n(v: string | number | null | undefined): number {
  const x = Number(v ?? 0);
  return Number.isFinite(x) ? x : 0;
}

function moneyRound(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * Resolve balance from ticket columns. Does not invent a second formula
 * against payments — those totals are owned by recalculateTicketPaymentTotals.
 */
export function resolveTicketBalance(ticket: TicketBalanceInput): TicketBalance {
  const orderTotal = moneyRound(n(ticket.total));
  const paidTotal = moneyRound(n(ticket.paidTotal));
  // Server balanceDue is authoritative when present (including negative credit).
  const hasServerBalance =
    ticket.balanceDue !== undefined && ticket.balanceDue !== null && ticket.balanceDue !== '';
  const balanceDue = hasServerBalance
    ? moneyRound(n(ticket.balanceDue))
    : moneyRound(orderTotal - paidTotal);

  const amountToCollect = Math.max(balanceDue, 0);
  const creditOwed = Math.max(-balanceDue, 0);

  return {
    orderTotal,
    paidTotal,
    balanceDue,
    amountToCollect,
    creditOwed,
    isPaidInFull: balanceDue <= 0.009 && creditOwed <= 0.009,
    isOverpaid: creditOwed > 0.009,
  };
}
